import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { DISABLED, FOCUS_RING, HOVER_OUTLINE } from './states';

/**
 * Primitive de champ (audit UX/UI passe 2, B1). Remplace les trois
 * définitions locales et légèrement différentes de `inputClass`
 * (RecipeDetail.tsx, RecipeForm.tsx) et `fieldClass` (CookLogForm.tsx) par
 * une seule base, déclinée en deux densités plutôt qu'en un style unique :
 * l'édition sur place (`compact`, EditableField de RecipeDetail.tsx) reste
 * délibérément plus discrète qu'un champ de formulaire plein (`default`,
 * RecipeForm.tsx, CookLogForm.tsx, ImportUrlForm.tsx) — la distinction entre
 * lecture et formulaire (B5) reste un chantier passe 4, mais la densité, elle,
 * doit déjà venir d'un seul endroit.
 */
export type FieldDensity = 'default' | 'compact';

// `border-border-interactive` et non `border` : un champ doit se voir (WCAG 1.4.11,
// 3:1 minimum contre la surface — US-24).
const BASE =
  `rounded-control border border-border-interactive bg-transparent text-foreground placeholder:text-foreground-muted ${HOVER_OUTLINE} ${FOCUS_RING} ${DISABLED}`;

// Deux densités, deux planchers tactiles (US-25) : 48 px pour un champ de
// formulaire plein, 44 px pour l'édition sur place — jamais moins. `compact`
// valait `px-2 py-1`, soit 26 px de haut, très en dessous du §4.
const DENSITY_CLASSES: Record<FieldDensity, string> = {
  default: 'min-h-touch w-full px-3 py-2 text-sm',
  compact: 'min-h-touch-min px-2 py-1 text-sm',
};

export function fieldClasses(density: FieldDensity = 'default', className = ''): string {
  return `${BASE} ${DENSITY_CLASSES[density]} ${className}`.trim();
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  density?: FieldDensity;
}

export function TextInput({ density = 'default', className = '', ...props }: TextInputProps): React.JSX.Element {
  return <input className={fieldClasses(density, className)} {...props} />;
}

export interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  density?: FieldDensity;
}

export function TextareaField({ density = 'default', className = '', ...props }: TextareaFieldProps): React.JSX.Element {
  return <textarea className={fieldClasses(density, className)} {...props} />;
}

// `SelectField` a disparu en US-25 : une liste déroulante a besoin d'un
// habillage propre (flèche redessinée, `appearance-none`, plancher tactile)
// que la base commune des champs ne donne pas. Voir `ui/Select.tsx`.
